import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'
import { Button, Modal, State, Toast } from '@seiue/ui'
import { Form } from 'antd'
import {
  SchoolPlugin,
  SchoolPluginScope,
  SourceEnum,
  pluginApi,
  reflectionApi,
} from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'
import { useEffect, useState } from 'react'

import { UserPluginContext, handleSavedPluginScopes } from '../utils/util'

import { UserPluginContent } from './components/UserPluginContent'

/**
 * 编辑推荐应用弹窗
 *
 * @param param - 参数
 * @param param.onClose - 关闭方法
 * @param param.schoolPlugin - 编辑的应用
 * @returns 组件
 */
export const EditUserPluginModal: React.FC<{
  onClose: () => void
  schoolPlugin?: SchoolPlugin
}> = ({ onClose, schoolPlugin }) => {
  const isEdit = schoolPlugin !== undefined
  const id = schoolPlugin?.id

  const [form] = Form.useForm()

  const initFormValues = schoolPlugin !== undefined ? { ...schoolPlugin } : {}

  const [uploadIcon, setUploadIcon] = useState<string | undefined>(
    !schoolPlugin?.icon ? undefined : schoolPlugin.icon
  )

  const [scopes, setScopes] = useState<SchoolPluginScope>(
    schoolPlugin?.scopes !== undefined
      ? schoolPlugin.scopes
      : { ids: [], studentIds: [], teacherIds: [] }
  )

  const { data: tags, loading } = pluginApi.useQuerySchoolPluginTags()

  const { data: selectInfo, loading: selectLoading } =
    reflectionApi.useCountReflections(
      { idIn: schoolPlugin?.scopes?.ids.join(',') },
      { disable: !isEdit || schoolPlugin.scopes === undefined }
    )

  const [selectNames, setSelectNames] = useState<string[]>(
    isEdit && selectInfo ? selectInfo.names : []
  )

  const [showCard, setShowCard] = useState<boolean>(scopes.ids.length > 0)

  const { api: createSchoolPlugin } = pluginApi.useCreateSchoolPlugin({
    reload: [pluginApi.querySchoolPlugins, pluginApi.querySchoolPluginTags],
  })
  const { api: updateSchoolPlugin } = pluginApi.useUpdateSchoolPlugin({
    reload: [pluginApi.querySchoolPlugins, pluginApi.querySchoolPluginTags],
  })

  const onClickSave = async () => {
    form.submit()

    form.validateFields().then(async (values) => {
      const saveData = {
        ...values,
        description: values.description || '',
        entryUrls: {
          web: values.entryUrls.web,
          mobile: values.entryUrls.mobile || '',
        },
        tagId: values.tagId || 0,
        scopes: handleSavedPluginScopes(scopes, showCard),
      }

      if (isEdit && id) {
        await updateSchoolPlugin({ id, payload: saveData })
      } else {
        await createSchoolPlugin({ source: SourceEnum.User, payload: saveData })
      }

      onClose()
      Toast.success(isEdit ? '保存成功' : '添加成功')
    })
  }

  const onClickCancel = () => {
    if (form.getFieldValue('label') === undefined) {
      onClose()
      return
    }

    Modal.confirm({
      title: '确认离开该界面吗?',
      content: '正在离开该界面，离开后修改数据不会保存，确认离开吗？',
      onOk: () => onClose(),
    })
  }

  useEffect(() => {
    if (!selectLoading && isEdit && !!selectInfo) {
      setSelectNames(selectInfo.names)
    }
  }, [selectLoading, selectInfo, isEdit])

  if (loading || selectLoading) return <State.Loading />

  return (
    <EntryComponentsWrapper>
      <UserPluginContext.Provider
        value={{
          initFormValues,
          uploadIcon,
          setUploadIcon,
          tags,
          scopes,
          setScopes,
          selectNames,
          setSelectNames,
          showCard,
          setShowCard,
        }}
      >
        <ModalLayout
          title={
            <TaskHeaderLeft
              title={isEdit ? '应用详情' : '创建应用'}
              onClose={onClose}
            />
          }
          headerRight={
            <>
              <Button
                type="gray"
                style={{ marginRight: '12px' }}
                onClick={onClickCancel}
              >
                {'取消'}
              </Button>
              <Button
                type="primary"
                style={{ marginRight: 0 }}
                onClick={onClickSave}
              >
                {'保存'}
              </Button>
            </>
          }
          contentStyle={{
            padding: '20px 240px',
            height: 'calc(100vh - 72px)',
            overflow: 'auto',
          }}
          content={<UserPluginContent form={form} />}
        />
      </UserPluginContext.Provider>
    </EntryComponentsWrapper>
  )
}
